import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, ReactNode } from 'react'
import { WithClassName } from '~/types/utils'
import { UpgradeOfferDialog } from './UpgradeOfferDialog'

type Props = WithClassName & {
  /** What is missing. May be empty, when nothing behind the wall has a product name — see `upgradeDialogCopy`. */
  offers: UpgradeOffer[]
  /** Names the thing the reader just tried to open, when the surface can be more specific than the offer. */
  context?: ReactNode
}

/**
 * The upgrade offer as a card: the one frame the pitch wears, wherever it appears.
 *
 * There are two surfaces that show it and they are deliberately different things — a modal over a page
 * worth keeping (the Catalog), and a blurred page there is nothing to keep (an ad unit tab). What must not
 * differ is the card itself, and it used to: the modal took `Modal.Body`'s 24px and a 4px corner while the
 * locked panel drew its own 32px and a 12px one, so the same offer arrived looking like two designs. This
 * is that frame, owned in one place, so a change to it lands on both.
 *
 * The width is `w-[860px] max-w-full` — a *definite* 860 rather than `w-full` against a max — because
 * `UpgradeOfferDialog`'s text column is a `@container`, and a size-contained box contributes nothing to an
 * ancestor's shrink-to-fit sizing. A card whose width comes from its parent therefore collapses to the lock
 * icon's width wherever that parent is itself shrink-to-fit, wrapping every word of the pitch onto its own
 * line: that is what the ad unit tabs did, and what the modal would do too, since react-modal's content box
 * sizes to its child. 860 is the two columns — 614 of text, 56 of gap, a 190 lock — and `max-w-full` is what
 * still lets the whole thing shrink on a page narrower than that.
 */
export const UpgradeOfferCard: FunctionComponent<Props> = ({ className, offers, context }) => (
  <div className={cn('border-platinum w-[860px] max-w-full rounded-xl border bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.12)]', className)}>
    <UpgradeOfferDialog offers={offers} context={context} />
  </div>
)
